import TrendButton from "../TrendButton";
import TrendBadge from "../TrendBadge";
import { fmtInt } from "../../lib/format";

const STATUS = {
  Compliant: { accent: "var(--success-500)", value: "var(--success-600)", track: "rgba(62,124,79,0.16)" },
  "At risk": { accent: "var(--amber-500)", value: "var(--tan)", track: "rgba(226,152,37,0.18)" },
  "Non-compliant": { accent: "var(--critical-600)", value: "var(--critical-600)", track: "rgba(165,48,15,0.14)" },
};

/**
 * ControlCardCiso — per-control compliance card in the CISO widget language.
 * Body click opens the trend sheet; "Endpoints" drills to the record table.
 */
export default function ControlCardCiso({ c, onOpenSheet, onDrill }) {
  const s = STATUS[c.status] || STATUS["At risk"];
  const mini = [
    { label: "Healthy", value: c.healthy, color: "var(--success-600)" },
    { label: "Unhealthy", value: c.unhealthy, color: "var(--tan)" },
    { label: "Missing", value: c.missing, color: "var(--critical-600)" },
  ];
  return (
    <div className="ciso-stat" data-testid={`control-card-${c.id}`} style={{ cursor: "default" }}>
      <div className="ciso-stat__bar" style={{ background: s.accent }} />

      <button
        type="button"
        onClick={() => onOpenSheet(c)}
        data-testid={`control-card-open-${c.id}`}
        className="text-left w-full"
        style={{ background: "transparent", padding: 0, border: 0, cursor: "pointer" }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 16, color: "var(--ink)", fontVariant: "small-caps", letterSpacing: "0.03em" }}>
              {c.control}
            </div>
            <div className="text-[12.5px] truncate" style={{ color: "var(--text-muted)" }}>{c.vendor}</div>
          </div>
          <span
            className="shrink-0 text-[11px] font-bold"
            style={{
              padding: "2px 9px",
              borderRadius: "var(--radius-pill)",
              background: c.status === "Compliant" ? "var(--success-50)" : c.status === "At risk" ? "var(--warning-50)" : "var(--critical-50)",
              color: s.value,
              whiteSpace: "nowrap",
            }}
          >
            {c.status}
          </span>
        </div>

        <div className="flex items-end gap-2" style={{ marginTop: 12 }}>
          <div className="ciso-num" style={{ fontSize: 34, fontWeight: 600, lineHeight: 1, letterSpacing: "-0.03em", color: s.value }}>
            {c.health_pct}%
          </div>
          <div className="text-[12px] font-bold ciso-cap" style={{ color: "var(--text-muted)", paddingBottom: 3 }}>
            healthy
          </div>
          <div style={{ marginLeft: "auto", paddingBottom: 2 }}>
            <TrendBadge trend={c.trend} size="sm" testid={`control-card-${c.id}-trend`} />
          </div>
        </div>

        <div
          data-testid={`progress-track-${c.id}`}
          style={{ position: "relative", height: 7, width: "100%", overflow: "hidden", borderRadius: 999, background: s.track, marginTop: 10 }}
        >
          <div
            data-testid={`progress-fill-${c.id}`}
            data-status={c.status}
            style={{ height: "100%", width: `${Math.min(100, c.health_pct)}%`, background: s.accent, borderRadius: 999, transition: "width 300ms" }}
          />
        </div>

        <div className="text-[12.5px]" style={{ color: "var(--text-secondary)", marginTop: 12 }}>
          In-scope <strong className="ciso-num" style={{ color: "var(--ink)" }}>{fmtInt(c.in_scope)}</strong>
        </div>
        <div className="grid grid-cols-3 gap-2" style={{ marginTop: 8 }}>
          {mini.map((m) => (
            <div key={m.label}>
              <div className="text-[11.5px] font-bold ciso-cap" style={{ color: "var(--text-muted)" }}>{m.label}</div>
              <div className="ciso-num" style={{ fontSize: 15, fontWeight: 600, color: m.color }}>{fmtInt(m.value)}</div>
            </div>
          ))}
        </div>
      </button>

      <div className="flex items-center justify-between" style={{ marginTop: 14, borderTop: "1px solid var(--divider)", paddingTop: 12 }}>
        <span className="text-[12px]" style={{ color: "var(--text-muted)" }}>
          Owner: <strong style={{ color: "var(--text-secondary)" }}>{c.owner}</strong>
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid={`control-card-cta-${c.id}`}
            onClick={() => onDrill(c)}
            className="inline-flex items-center gap-1 text-[12px] font-bold"
            style={{ color: "var(--rust-600)", background: "transparent", border: 0, cursor: "pointer" }}
          >
            Endpoints <i className="ph-bold ph-arrow-right" style={{ fontSize: 12 }} />
          </button>
          <TrendButton
            widgetId={`control-${c.id}`}
            label={`${c.control} · Healthy %`}
            module="control"
            unit="percent"
            current={c.health_pct}
            testid={`control-card-${c.id}-view-trend`}
          />
        </div>
      </div>
    </div>
  );
}
